import classNames from 'classnames';
import React, { FocusEvent, KeyboardEvent, useEffect } from 'react';
import ReactSelect, { OptionProps, components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { CheckIcon } from '../icons';
import { fixNameEnum } from '../utils';
import { createOption, styles } from './SelectPaginate';

export interface SelectWithCreatableOption<T, K = string> {
  value: T;
  label: K;
}

export function getSelectOptionByEnum<T, L = string>(
  itemEnum: object,
  showNameEnumInLabel = false,
  withFixName = true,
): SelectWithCreatableOption<T, L>[] {
  if (showNameEnumInLabel) {
    return Object.entries(itemEnum).map(item => ({
      label: (withFixName ? fixNameEnum(item[0]) : item[0]) as L,
      value: item[1] as T,
    }));
  }
  return Object.values(itemEnum).map(item => ({
    label: (withFixName ? fixNameEnum(item) : item) as L,
    value: item as T,
  }));
}

export function getSelectValueByEnum<T, L = string>(
  itemEnum: string,
  allEnum?: object,
  withFixNameEnum = true,
): SelectWithCreatableOption<T, L> {
  if (allEnum) {
    return (
      Object.entries(allEnum)
        .filter(item => item[1] === itemEnum)
        .map(item => ({
          label: (withFixNameEnum ? fixNameEnum(item[0]) : item[0]) as L,
          value: item[1] as T,
        }))[0] ?? {
        label: (withFixNameEnum ? fixNameEnum(itemEnum) : itemEnum) as L,
        value: itemEnum as T,
      }
    );
  }

  return {
    label: (withFixNameEnum ? fixNameEnum(itemEnum) : itemEnum) as L,
    value: itemEnum as T,
  };
}

const Option = ({ children, isSelected, ...props }: OptionProps) => (
  <components.Option
    isSelected={isSelected}
    className={classNames('text-[#6366F1]', props.className)}
    {...props}
  >
    {isSelected && (
      <span className="absolute flex items-center justify-center right-4">
        <img src={CheckIcon} alt="selected" />
      </span>
    )}
    {children}
  </components.Option>
);

export interface SelectWithCreatableProps<T, K> {
  name?: string;
  id?: string;
  onChange?: (newValue: SelectWithCreatableOption<T, K> | null) => void;
  options?: SelectWithCreatableOption<T, K>[];
  defaultValue?: SelectWithCreatableOption<T, K>;
  value?: SelectWithCreatableOption<T, K> | SelectWithCreatableOption<T, K>[];
  className?: string;
  isLoading?: boolean;
  label?: string;
  classNameLabel?: string;
  classNameSelect?: string;
  error?: string;
  isClearable?: boolean;
  positionMenu?: 'up' | 'down';
  position?: 'relative' | 'absolute';
  disabled?: boolean;
  withCreatable?: boolean;
  isMulti?: boolean;
  onChangeMulti?: (item: SelectWithCreatableOption<T, K>[] | null) => void;
  placeholder?: string;
  withBorder?: boolean;
  icon?: string;
  autoHeight?: boolean;
  bgTransparent?: boolean;
  rounded?: 'full' | 'right' | 'left';
  noOptionsMessage?: React.ReactNode;
}

export const SelectWithCreatable = <T, K = string>({
  onChange,
  defaultValue,
  options,
  className,
  isLoading,
  value,
  label,
  classNameLabel,
  classNameSelect,
  name,
  id,
  icon,
  error,
  positionMenu,
  disabled,
  placeholder,
  onChangeMulti,
  noOptionsMessage,
  isMulti = false,
  rounded = 'full',
  withBorder = false,
  autoHeight = true,
  isClearable = false,
  bgTransparent = false,
  withCreatable = false,
  position = 'relative',
}: SelectWithCreatableProps<T, K>) => {
  const [inputValue, setInputValue] = React.useState('');
  const [multiValue, setMultiValue] = React.useState<SelectWithCreatableOption<T, K>[]>(
    value ? (Array.isArray(value) ? value : [value].filter(Boolean)) : [],
  );

  useEffect(() => {
    setMultiValue(value ? (Array.isArray(value) ? value : [value].filter(Boolean)) : []);
  }, [value]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!inputValue) return;
    switch (event.keyCode) {
      case 13: //Enter
      case 10:
      case 9: //Tab
      case 188: //, // case 229: //, mobile
        setMultiValue(prev => {
          if (prev.map(item => item.value as string).includes(inputValue)) {
            return prev;
          }
          return [...prev, createOption(inputValue)] as SelectWithCreatableOption<T, K>[];
        });

        setInputValue('');
        event.preventDefault();
    }
  };

  const handleOnInputChange = (newValue: string) => {
    setInputValue(newValue);
    if (!newValue || !withCreatable || !isMulti) return;
    // for mobile
    const lastSymbol = newValue[newValue.length - 1];
    if (lastSymbol === ',') {
      setMultiValue(prev => {
        if (prev.map(item => item.value as string).includes(inputValue)) {
          return prev;
        }
        return [...prev, createOption(inputValue)] as SelectWithCreatableOption<T, K>[];
      });

      setInputValue('');
    }
  };

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue.trim() || !withCreatable || !isMulti) return;
    // for mobile
    setMultiValue(prev => {
      if (prev.map(item => item.value as string).includes(inputValue)) {
        return prev;
      }
      return [...prev, createOption(inputValue)] as SelectWithCreatableOption<T, K>[];
    });

    setInputValue('');
  };

  const handleOnChange = (items: SelectWithCreatableOption<T, K>[]) => {
    setMultiValue(items);
    onChangeMulti && onChangeMulti(items);
  };

  const CurrentSelect =
    withCreatable === true ? (
      <CreatableSelect
        name={name}
        id={id}
        onBlur={handleOnBlur}
        placeholder={placeholder || 'Select or write...'}
        components={
          !isMulti ? { Option } : !options ? { Option, DropdownIndicator: null } : { Option }
        }
        menuIsOpen={isMulti ? false : undefined}
        onChange={isMulti ? (handleOnChange as any) : (onChange as any)}
        onInputChange={handleOnInputChange}
        inputValue={inputValue}
        defaultValue={defaultValue}
        value={isMulti ? multiValue : value}
        options={options}
        styles={styles({
          positionMenu,
          creatable: true,
          position,
          withBorder,
          autoHeight,
          bgTransparent,
          rounded,
          isClearable,
          isError: !!error,
        })}
        isLoading={isLoading}
        isDisabled={disabled}
        isMulti={isMulti}
        isClearable={isMulti || isClearable}
        onKeyDown={!isMulti ? undefined : handleKeyDown}
        noOptionsMessage={noOptionsMessage ? () => noOptionsMessage : undefined}
        className={classNames(
          'border-[#6366F1] flex-auto',
          {
            'pl-8': icon,
          },
          label === undefined && !icon && !error ? classNameSelect : undefined,
        )}
      />
    ) : (
      <ReactSelect
        name={name}
        id={id}
        onBlur={handleOnBlur}
        placeholder={placeholder}
        components={
          !isMulti ? { Option } : !options ? { Option, DropdownIndicator: null } : { Option }
        }
        onChange={isMulti ? (handleOnChange as any) : (onChange as any)}
        onInputChange={handleOnInputChange}
        inputValue={inputValue}
        defaultValue={defaultValue}
        value={isMulti ? multiValue : value}
        options={options}
        styles={styles({
          positionMenu,
          position,
          withBorder,
          autoHeight,
          bgTransparent,
          rounded,
          isClearable,
          isError: !!error,
        })}
        isLoading={isLoading}
        isDisabled={disabled}
        isMulti={isMulti}
        isClearable={isMulti || isClearable}
      />
    );

  if (label !== undefined || icon || error) {
    return (
      <div
        className={classNames(
          'flex',
          {
            'flex-col': !className?.includes('flex-row'),
            'w-full': !className?.includes('w-'),
            relative: icon,
          },
          className,
        )}
      >
        {label !== undefined && (
          <span
            className={classNames(
              'text-gray-900 text-[10px] leading-[18px] font-meduim opacity-64 uppercase mb-2',
              classNameLabel,
            )}
          >
            {label}
          </span>
        )}
        {CurrentSelect}
        {icon && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-4">
            <img src={icon} alt={name || 'icon'} className="h-4 w-4 min-w-[16px] min-h-[16px]" />
          </span>
        )}
        {error && <span className="mt-2 text-sm text-red-500">{error}</span>}
      </div>
    );
  }

  return CurrentSelect;
};
